import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {
  TreeNode,
  TagItem,
  MetaDataField,
} from '../../../api/types/production.type';
import {ProductionStatus} from './CreateNewProductionUtils';
import {ISelectItem} from '../../../shared/types';

// Convert flat tag tree data to hierarchical structure used by components
export const transformTagTreeData = (
  flatArray: TreeNode[],
  treeId?: string
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const itemMap = new Map();
  const rootItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  // First pass: create all items, keeping only first occurrence of duplicate IDs
  flatArray.forEach((item) => {
    // Only add if this ID hasn't been seen before (keep first occurrence)
    if (!itemMap.has(item.id)) {
      itemMap.set(item.id, {
        ...item,
        id: item.id,
        label: item.text,
        treeId: treeId,
        parentGroupId: item.parent,
        children: [],
      } as unknown as TreeViewBaseItem<ExtendedTreeItemProps>);
    }
  });

  // Second pass: build hierarchy, only process items that were added to the map
  const processedInSecondPass = new Set<string>();
  flatArray.forEach((item) => {
    // Skip if this item was a duplicate and wasn't added to the map, or already processed
    if (!itemMap.has(item.id) || processedInSecondPass.has(item.id)) {
      return;
    }

    processedInSecondPass.add(item.id);
    const treeItem = itemMap.get(item.id);
    if (
      item.parent === '#' ||
      item.parent === null ||
      item.parent === undefined
    ) {
      rootItems.push(treeItem);
    } else {
      const parent = itemMap.get(item.parent);
      if (parent) parent.children.push(treeItem);
      else rootItems.push(treeItem);
    }
  });

  return rootItems;
};

// Remove spaces immediately after opening parenthesis and immediately before closing parenthesis
export const removeParenthesisSpaces = (
  text: string | null | undefined
): string => {
  if (!text) return '';
  return text.trim().replace(/\(\s/g, '(').replace(/\s\)/g, ')');
};

const STATUS_PATTERNS: Array<{pattern: string; code: ProductionStatus}> = [
  {pattern: 'In Progress', code: ProductionStatus.INPROGRESS},
  {pattern: 'Failed', code: ProductionStatus.FAILED},
  {pattern: 'Completed', code: ProductionStatus.COMPLETED},
  {pattern: 'Complete', code: ProductionStatus.COMPLETED},
];

// Parse status text to separate main text from status part
export const parseStatusText = (
  statusText: string | null | undefined
): {mainText: string; status: string; statusCode: ProductionStatus} | null => {
  if (!statusText) {
    return null;
  }

  const trimmed = statusText.trim();

  // Try to find a status pattern at the end of the text
  for (const {pattern, code} of STATUS_PATTERNS) {
    // eslint-disable-next-line security/detect-non-literal-regexp
    const regex = new RegExp(
      `(.+?)\\s+(${pattern.replace(/\s+/g, '\\s+')})$`,
      'i'
    );
    const match = trimmed.match(regex);
    if (match) {
      return {
        mainText: match[1].trim(),
        status: match[2].trim(),
        statusCode: code,
      };
    }
  }

  // If no pattern found, return the whole text as mainText with empty status
  return {
    mainText: trimmed,
    status: '',
    statusCode: ProductionStatus.DRAFT,
  };
};

// Convert TagItem to TreeViewBaseItem
export const convertTagItemToTreeView = (
  tag: TagItem
): TreeViewBaseItem<ExtendedTreeItemProps> => ({
  id: String(tag.tagId),
  label: tag.tagLabel,
});

// Convert TreeViewBaseItem to TagItem
export const convertTreeViewToTagItem = (
  item: TreeViewBaseItem<ExtendedTreeItemProps>
): TagItem => ({
  tagId: parseInt(item.id),
  tagLabel: item.label,
});

// Helper to find a node by ID in a tree structure
export const findNodeById = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  targetId: string
): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
  for (const node of nodes) {
    if (String(node.id) === targetId) return node;
    if (node.children) {
      const found = findNodeById(node.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

// Find multiple nodes by their IDs in a tree structure
export const findNodesByIds = (
  tree: TreeViewBaseItem<ExtendedTreeItemProps>[],
  ids: string[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const result: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
  const search = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    for (const node of nodes) {
      if (ids.includes(node.id)) {
        result.push(node);
      }
      if (node.children) {
        search(node.children);
      }
    }
  };
  search(tree);
  return result;
};

// Check if a node has any enabled children (recursively)
// A node is considered enabled if it's not disabled
export const hasEnabledChild = (
  node: TreeViewBaseItem<ExtendedTreeItemProps>
): boolean => {
  if (!node.children || node.children.length === 0) {
    // Leaf node: check if it's enabled
    return !node.disabled;
  }
  // Parent node: check if any child (recursively) is enabled
  return node.children.some((child) => hasEnabledChild(child));
};

// Map tag IDs from CSV to TagItem[] using specified tag tree
// Excludes parent/group nodes - only includes leaf nodes
export const mapIdsToTags = (
  idsCsv: string,
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[]
): TagItem[] => {
  const ids = (idsCsv || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!ids.length) return [];

  return ids
    .map((id) => {
      const tagNode = findNodeById(treeData || [], id);
      if (!tagNode) return null;
      // Exclude parent/group nodes - only include leaf nodes (not groups and have a parent)
      const isGroup = tagNode.isGroup === true || tagNode.isGroup === 'true';
      const isRoot = !tagNode.parentGroupId || tagNode.parentGroupId === '#';
      if (isGroup || isRoot) return null;
      return {tagId: parseInt(id), tagLabel: tagNode.label};
    })
    .filter((item): item is TagItem => item !== null);
};

// Convert metadata fields to flat tree items (no parent groups)
export const convertMetadataToTreeItems = (
  fields: MetaDataField[] | undefined,
  treeId?: string
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  if (!fields || fields.length === 0) return [];

  // Return flat list of fields (no grouping)
  return fields.map((field) => ({
    id: String(field.fieldId),
    label: field.fieldLabel,
    treeId,
  }));
};

// Get preselected items from CSV string
export const getPreselectedItems = (
  idsCsv: string,
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[]
): ISelectItem[] => {
  if (!idsCsv) return [];
  const ids = idsCsv
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!ids.length) return [];

  const items: ISelectItem[] = [];
  ids.forEach((id) => {
    const node = findNodeById(treeData, id);
    if (node) {
      items.push({id, name: node.label});
    }
  });
  return items;
};

/**
 * Filters TagItem array to only include tags that exist in the tree data
 * @param tags - Array of TagItem to filter
 * @param treeData - Tree data to validate tags against
 * @returns Filtered array of TagItem that exist in treeData (only leaf nodes, not groups or roots)
 */
export const filterValidTags = (
  tags: TagItem[],
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[]
): TagItem[] => {
  if (!tags || tags.length === 0) return [];
  if (!treeData || treeData.length === 0) return tags; // If no tree data, return as-is

  return tags.filter((tag) => {
    const tagNode = findNodeById(treeData, String(tag.tagId));
    if (!tagNode) return false;
    // Exclude parent/group nodes - only include leaf nodes
    const isGroup = tagNode.isGroup === true || tagNode.isGroup === 'true';
    const isRoot = !tagNode.parentGroupId || tagNode.parentGroupId === '#';
    return !isGroup && !isRoot;
  });
};

/**
 * Converts HTML paragraph tags to line breaks when hydrating from API.
 * Replaces <p></p> tags with newline characters for display in text fields.
 * Handles both HTML-encoded entities (&lt;p&gt;) and regular HTML tags (<p>).
 * Strips all HTML tags to ensure only plain text is returned.
 *
 * @param htmlString - The HTML string containing <p> tags (may be HTML-encoded).
 * @returns Text with <p> tags converted to line breaks and all HTML tags stripped.
 */
export const convertPTagsToLineBreaks = (htmlString: string): string => {
  if (!htmlString || htmlString.trim() === '') {
    return '';
  }

  // Step 1: Decode HTML entities first (e.g., &lt; becomes <, &gt; becomes >, &amp; becomes &)
  // Use a textarea element to decode HTML entities without parsing as HTML
  const decodeTmp = document.createElement('textarea');
  decodeTmp.innerHTML = htmlString;
  const decodedString = decodeTmp.value || htmlString;

  // Step 2: Process HTML tags - convert <p> tags to line breaks and strip all HTML tags
  // If the decoded string doesn't contain <p> tags, just strip all HTML and return
  if (!decodedString.includes('<p>') && !decodedString.includes('</p>')) {
    // Strip all HTML tags by extracting text content
    const stripTmp = document.createElement('DIV');
    stripTmp.innerHTML = decodedString;
    return (stripTmp.textContent || stripTmp.innerText || decodedString).trim();
  }

  // Replace <p> tags with newlines before stripping HTML
  // Replace closing </p> tags followed by opening <p> tags with newline
  // This handles cases like <p>Line 1</p><p>Line 2</p>
  const processedString = decodedString
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<\/p>/gi, '')
    .replace(/<p>/gi, '');

  // Step 3: Strip any remaining HTML tags that might be present
  // Create a temporary element to extract text content and remove all HTML tags
  const stripTmp = document.createElement('DIV');
  stripTmp.innerHTML = processedString;
  const plainText =
    stripTmp.textContent || stripTmp.innerText || processedString;

  // Trim any leading/trailing whitespace
  return plainText.trim();
};

/**
 * Wraps text content with specified HTML tag when saving to API.
 * Splits text by line breaks and wraps each line with the specified tag.
 * If text is empty or already wrapped with the same tag, returns as-is.
 *
 * @param text - The text content to wrap.
 * @param tag - The HTML tag name to wrap with (e.g., 'p', 'div', 'span'). Defaults to 'p'.
 * @returns Text with each line wrapped with the specified tag, or original text if empty/already wrapped.
 */
export const wrapTextWithTag = (
  text: string,
  tag: string = 'p'
): string | null => {
  if (!text || text.trim() === '') {
    return null;
  }

  const openingTag = `<${tag}>`;
  const closingTag = `</${tag}>`;
  const trimmedText = text.trim();

  // Split by line breaks (handles \n, \r\n, \r)
  const lines = text.split(/\r?\n|\r/).filter((line) => line.trim() !== '');

  // If no line breaks, check if single line is already wrapped
  if (lines.length === 0) {
    // Check if already wrapped
    if (
      trimmedText.toLowerCase().startsWith(openingTag.toLowerCase()) &&
      trimmedText.toLowerCase().endsWith(closingTag.toLowerCase())
    ) {
      return text; // Already wrapped, return as-is
    }
    return `${openingTag}${text}${closingTag}`;
  }

  // Check if all lines are already wrapped with the specified tag
  let allLinesWrapped = true;
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine) {
      // Check if this line is already wrapped
      const isWrapped =
        trimmedLine.toLowerCase().startsWith(openingTag.toLowerCase()) &&
        trimmedLine.toLowerCase().endsWith(closingTag.toLowerCase());
      if (!isWrapped) {
        allLinesWrapped = false;
        break;
      }
    }
  }

  // If all lines are already wrapped, return as-is
  if (allLinesWrapped) {
    return text;
  }

  // Wrap each line that isn't already wrapped
  const wrappedLines = lines
    .map((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        return '';
      }
      // Check if already wrapped
      const isWrapped =
        trimmedLine.toLowerCase().startsWith(openingTag.toLowerCase()) &&
        trimmedLine.toLowerCase().endsWith(closingTag.toLowerCase());
      // Return as-is if already wrapped, otherwise wrap it
      return isWrapped
        ? trimmedLine
        : `${openingTag}${trimmedLine}${closingTag}`;
    })
    .filter((line) => line !== '');

  return wrappedLines.join('');
};
